import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";
import { submissionFileUrl, gradeLabel } from "@/lib/exercises";
import { FileLinks, SubStatusBadge } from "@/components/exercises/common";
import StudentAvatar from "@/components/StudentAvatar";
import StaffUploadDialog from "@/components/exercises/StaffUploadDialog";
import { useAuth, isAdminOrSegreteria } from "@/lib/auth";
import { Upload } from "lucide-react";

function ReviewForm({ sub, onDone }) {
  const [grade, setGrade] = useState(sub.grade ?? "");
  const [lode, setLode] = useState(!!sub.lode);
  const [feedback, setFeedback] = useState(sub.feedback || "");
  const mut = useMutation({
    mutationFn: async (status) => (await api.patch(`/submissions/${sub.id}/review`, { status, grade: grade === "" ? null : Number(grade), lode, feedback: feedback || null })).data,
    onSuccess: () => { toast.success("Valutazione salvata"); onDone(); },
    onError: (e) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Voto non valido (0–30)"),
  });
  return (
    <div className="mt-2 p-3 rounded-lg bg-stone-50 border border-stone-200 space-y-2">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs">Voto <Input type="number" min="0" max="30" value={grade} onChange={(e) => setGrade(e.target.value)} className="w-20 h-8" data-testid={`review-grade-${sub.id}`}/> /30</div>
        {Number(grade) === 30 && <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={lode} onChange={(e) => setLode(e.target.checked)} data-testid={`review-lode-${sub.id}`}/> lode</label>}
      </div>
      <Textarea rows={2} value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Commento per lo studente" data-testid={`review-feedback-${sub.id}`}/>
      <div className="flex gap-2">
        <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" disabled={mut.isPending} onClick={() => mut.mutate("approvato")} data-testid={`review-approve-${sub.id}`}>Approva</Button>
        <Button size="sm" variant="outline" className="text-amber-800 border-amber-300" disabled={mut.isPending} onClick={() => mut.mutate("da_rivedere")} data-testid={`review-revise-${sub.id}`}>Da rivedere</Button>
      </div>
    </div>
  );
}

export default function SubmissionsDialog({ exercise, courseId, onClose }) {
  const qc = useQueryClient();
  const [reviewing, setReviewing] = useState(null);
  const [uploadFor, setUploadFor] = useState(null);
  const { user } = useAuth();
  const staff = isAdminOrSegreteria(user);
  const { data } = useQuery({ queryKey: ["submissions", exercise.id], queryFn: async () => (await api.get(`/exercises/${exercise.id}/submissions`)).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["submissions", exercise.id] }); qc.invalidateQueries({ queryKey: ["exercises", courseId] }); setReviewing(null); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="submissions-dialog">
        <DialogHeader>
          <DialogTitle>Consegne · {exercise.title}</DialogTitle>
          <DialogDescription>{data ? `${data.submissions.length} consegnati · ${data.missing.length} mancanti` : "Caricamento…"}</DialogDescription>
        </DialogHeader>
        <ul className="divide-y divide-stone-100">
          {(data?.submissions || []).map((s) => (
            <li key={s.id} className="py-3" data-testid={`submission-${s.id}`}>
              <div className="flex items-center gap-3">
                <StudentAvatar studentId={s.student_id} photoPath={s.photo_path} name={s.student_name} className="w-8 h-8 rounded-full text-[10px]"/>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium flex items-center gap-2 flex-wrap">{s.student_name} <SubStatusBadge status={s.status} late={s.late}/>{gradeLabel(s) && <span className="text-xs font-semibold text-[#1C352D]">{gradeLabel(s)}</span>}</div>
                  <div className="text-[11px] text-stone-500">{fmtDateTime(s.submitted_at)}{s.attempts > 1 && ` · consegna n. ${s.attempts}`}{s.uploaded_by_name && ` · caricata da ${s.uploaded_by_name}`}</div>
                </div>
                {staff && s.status !== "approvato" && (
                  <Button size="sm" variant="ghost" className="rounded-full" title="Carica/modifica per lo studente" onClick={() => setUploadFor({ student: { id: s.student_id, name: s.student_name }, sub: s })} data-testid={`submission-staff-upload-${s.id}`}><Upload className="w-4 h-4"/></Button>
                )}
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setReviewing(reviewing === s.id ? null : s.id)} data-testid={`submission-review-${s.id}`}>Valuta</Button>
              </div>
              <div className="pl-11 mt-1.5 space-y-1.5">
                {s.text && <p className="text-sm text-stone-700 whitespace-pre-line">{s.text}</p>}
                <FileLinks files={s.files} urlFor={(fid) => submissionFileUrl(s.id, fid)} testPrefix="submission-file"/>
                {s.feedback && reviewing !== s.id && <div className="text-xs text-stone-600 italic">Commento: {s.feedback}</div>}
                {reviewing === s.id && <ReviewForm sub={s} onDone={refresh}/>}
              </div>
            </li>
          ))}
        </ul>
        {data?.missing?.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1.5">Non hanno ancora consegnato</div>
            <div className="flex flex-wrap gap-1.5" data-testid="submissions-missing">
              {data.missing.map((m) => staff ? (
                <button key={m.id} onClick={() => setUploadFor({ student: { id: m.id, name: `${m.last_name} ${m.first_name}` } })} className="text-xs px-2 py-1 rounded-full bg-stone-100 text-stone-700 hover:bg-[#1C352D] hover:text-white transition-colors inline-flex items-center gap-1" data-testid={`missing-upload-${m.id}`}>
                  <Upload className="w-3 h-3"/>{m.last_name} {m.first_name}
                </button>
              ) : <span key={m.id} className="text-xs px-2 py-1 rounded-full bg-stone-100 text-stone-700">{m.last_name} {m.first_name}</span>)}
            </div>
            {staff && <p className="text-[11px] text-stone-500 mt-1.5">Tocca un nome per caricare la consegna al posto dello studente.</p>}
          </div>
        )}
      </DialogContent>
      {uploadFor && <StaffUploadDialog exercise={exercise} student={uploadFor.student} sub={uploadFor.sub} onClose={() => setUploadFor(null)} onDone={() => { setUploadFor(null); refresh(); }}/>}
    </Dialog>
  );
}
